/**
 * Scene manager: owns the lifecycle of screens that need more than DOM (the match scene owns the loop,
 * renderer and driver). DOM-only screens need no Scene. It is the ONLY writer of `ui.screen`: every navigation
 * (UiActions.goto/startMatch/quitMatch…) goes through `goto`, so exit/enter hooks always run in order and the
 * audio bridge hears about every screen change.
 */
import type { AudioBridge } from '../audio-bridge/types';
import type { UiStore } from '../ui-model/store';
import type { ScreenId } from '../ui-model/types';

export interface Scene {
  readonly id: ScreenId;
  enter(): void | Promise<void>;
  exit(): void;
}

export class SceneManager {
  private readonly scenes = new Map<ScreenId, Scene>();
  private current: Scene | null = null;
  private pending: Promise<void> = Promise.resolve();

  constructor(
    private readonly ui: UiStore,
    private readonly audio: Pick<AudioBridge, 'onScreen'>,
  ) {}

  register(scene: Scene): void {
    this.scenes.set(scene.id, scene);
  }

  get scene(): Scene | null {
    return this.current;
  }

  /** Navigations are serialized: a goto issued while a scene is still entering runs after it. */
  goto(id: ScreenId): Promise<void> {
    this.pending = this.pending.then(async () => {
      this.current?.exit();
      this.current = this.scenes.get(id) ?? null;
      this.ui._setScreen(id);
      this.audio.onScreen(id);
      await this.current?.enter();
    });
    return this.pending;
  }
}
